/**
 * Where to resume a flight checkout after a setup task that leaves the site
 * (Mono bank linking, Paystack card linking). Those redirects reload the app
 * and wipe the in-memory booking, so the gate saves this before starting any
 * task.
 *
 * Only a hint. The booking id is never trusted on its own: resuming re-fetches
 * the booking from the server, which checks it belongs to the signed-in
 * customer, and only an unpaid booking is restored. Payment endpoints keep
 * their own ownership and already-paid checks.
 *
 * sessionStorage, so it survives a same-tab redirect but not a new tab or a
 * closed browser.
 */
const KEY = "visaro.checkout_resume";
/** Trips fares go stale; past this the customer should search again. */
const MAX_AGE_MS = 45 * 60 * 1000;

export type CheckoutResume = {
  userId: string;
  bookingId: string;
  returnTo: string;
  returnState?: unknown;
  savedAt: number;
};

export function saveCheckoutResume(entry: Omit<CheckoutResume, "savedAt">) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...entry, savedAt: Date.now() }));
  } catch {
    // Storage blocked: the customer can still search again.
  }
}

export function readCheckoutResume(
  userId: string | number | null | undefined,
): CheckoutResume | null {
  if (userId == null) return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CheckoutResume;
    const fresh = Date.now() - Number(entry.savedAt) < MAX_AGE_MS;
    if (String(entry.userId) !== String(userId) || !fresh || !entry.bookingId) {
      clearCheckoutResume();
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function clearCheckoutResume() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
